import { useState, useEffect, useCallback } from 'react';
import { submitSurvey, saveSurveyDraft, getStudy, getUserSurveyResponses } from '../../../../Config/api';
import { calculateFormProgress } from '../ProgressCalculator';
import { STUDY_FORMS } from '../DynamicForm/constant';

// Helper function to get study form by ID
const getStudyFormById = (studyId) => {
    const id = String(studyId);
    return STUDY_FORMS[id] || null;
};

export const useSurveyForm = (userId, studyId, subject, isEditMode = false, isViewOnly = false, userRole = '') => {
    console.log('=== USESURVEYFORM HOOK ===');
    console.log('Received studyId:', studyId);
    console.log('Received userId:', userId);
    console.log('Received subject:', subject);
    console.log('isEditMode:', isEditMode);
    console.log('isViewOnly:', isViewOnly);
    console.log('userRole:', userRole);
    console.log('==========================');

    const isCRO = userRole === 'CRO Administrator' || userRole === 'CRO';

    const [studyData, setStudyData] = useState(null);
    const [currentSectionIndex, setCurrentSectionIndex] = useState(0);
    const [formData, setFormData] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [hasConsented, setHasConsented] = useState(true);
    const [isSavingDraft, setIsSavingDraft] = useState(false);
    const [lastSaveTime, setLastSaveTime] = useState(null);
    const [isEligible, setIsEligible] = useState(null); // null = not determined yet

    const autoSaveDraft = useCallback(async (data, sectionIndex, consented, eligible = true) => {
        if (isSubmitted) return;
        if (isCRO) {
            console.log('Auto-save skipped for CRO user');
            return;
        }

        try {
            setIsSavingDraft(true);

            const completionPercentage = calculateFormProgress(studyData, data);

            const draftData = {
                ...data,
                currentSectionIndex: sectionIndex,
                hasConsented: consented,
                isEligible: eligible,
                lastSaved: new Date().toISOString(),
                progress: completionPercentage,
            };

            await saveSurveyDraft(userId, studyId, subject.siteId, subject.subjectId, draftData);
            setLastSaveTime(new Date());
        } catch (error) {
            console.error('Auto-save failed:', error);
        } finally {
            setIsSavingDraft(false);
        }
    }, [userId, studyId, isSubmitted, studyData, subject, isCRO]);

    const fetchStudyData = useCallback(async () => {
        if (!userId || !studyId) {
            setIsLoading(false);
            setError('Missing required parameters');
            return;
        }

        try {
            setIsLoading(true);
            setError(null);

            console.log('=== FETCH STUDY DATA (FROM FRONTEND) ===');
            console.log('studyId:', studyId);

            // Get form structure from frontend constants
            const formStructure = getStudyFormById(studyId);

            if (!formStructure) {
                throw new Error(`No form definition found for studyId: ${studyId}`);
            }

            console.log('✓ Form structure loaded from frontend');
            console.log('Form ID:', formStructure.id);
            console.log('Number of sections:', formStructure.sections.length);

            // Set the form structure from static JSON
            setStudyData(formStructure);

            // If in edit mode, fetch submitted response data using separate API
            if (isEditMode) {
                console.log('=== EDIT MODE: Fetching submitted data ===');
                console.log('userId:', userId);
                console.log('studyId:', subject.studyId);
                console.log('subjectId:', subject.subjectId);
                console.log('siteId:', subject.siteId);
                console.log('API endpoint will be:', `/survey/user-responses/${userId}/${subject.studyId}${subject.subjectId ? `/${subject.subjectId}` : ''}${subject.siteId ? `/${subject.siteId}` : ''}`);
                
                try {
                    // Fetch submitted response from backend using getUserSurveyResponses
                    // Pass subjectId and siteId so CRO Admin can fetch data for specific subjects
                    const responseData = await getUserSurveyResponses(userId, subject.studyId, subject.subjectId, subject.siteId);
                    console.log('getUserSurveyResponses completed');
                    
                    console.log('=== SUBMITTED DATA RESPONSE ===');
                    console.log('Response type:', typeof responseData);
                    console.log('Response keys:', responseData ? Object.keys(responseData) : 'null');
                    console.log('Full response:', JSON.stringify(responseData, null, 2));
                    console.log('Response.data:', responseData?.data);
                    console.log('Response.data type:', typeof responseData?.data);
                    console.log('Response.data keys:', responseData?.data ? Object.keys(responseData.data) : 'null');
                    console.log('Response.hasResponses:', responseData?.hasResponses);
                    console.log('================================');

                    // Check if there's no response data
                    if (responseData?.hasResponses === false || responseData?.data === null) {
                        console.warn('⚠⚠⚠ No submitted response found for this subject');
                        console.log('Backend returned: hasResponses =', responseData.hasResponses, ', data =', responseData.data);
                        console.log('This means no one has submitted data for subject:', subject.subjectId);
                        // Continue with empty form - no data to load
                        setIsSubmitted(false);
                        setIsLoading(false);
                        return;
                    }

                    // Try multiple possible locations for submitted data
                    let submittedResponse = null;
                    
                    // Based on the API response structure, the path is: response.data.responseData
                    // The responseData object contains both metadata AND form fields at the same level
                    // So we should use response.data.responseData directly
                    if (responseData?.data?.responseData) {
                        submittedResponse = responseData.data.responseData;
                        console.log('Found data at: responseData.data.responseData');
                        console.log('Keys in responseData:', Object.keys(submittedResponse));
                    } else if (responseData?.data?.response) {
                        submittedResponse = responseData.data.response;
                        console.log('Found data at: responseData.data.response');
                    } else if (responseData?.responseData) {
                        submittedResponse = responseData.responseData;
                        console.log('Found data at: responseData.responseData');
                    } else if (responseData?.response) {
                        submittedResponse = responseData.response;
                        console.log('Found data at: responseData.response');
                    } else if (responseData?.data && responseData.data !== null) {
                        submittedResponse = responseData.data;
                        console.log('Found data at: responseData.data');
                    }

                    console.log('Submitted response type:', typeof submittedResponse);
                    console.log('Submitted response keys:', submittedResponse ? Object.keys(submittedResponse) : 'null');
                    console.log('Submitted response:', submittedResponse);
                    
                    // Log a few sample values to see if form fields exist
                    if (submittedResponse && typeof submittedResponse === 'object') {
                        console.log('Sample field check:');
                        console.log('  - inclusion_eligible:', submittedResponse.inclusion_eligible);
                        console.log('  - patient_name:', submittedResponse.patient_name);
                        console.log('  - data property:', submittedResponse.data);
                    }

                    if (submittedResponse && submittedResponse !== null) {
                        // Parse if string
                        const submitted = typeof submittedResponse === 'string' 
                            ? JSON.parse(submittedResponse) 
                            : submittedResponse;
                        
                        console.log('Parsed submitted data type:', typeof submitted);
                        console.log('Parsed submitted data keys:', submitted ? Object.keys(submitted) : 'null');
                        console.log('Full parsed data:', submitted);
                        
                        if (submitted && typeof submitted === 'object') {
                            // First, extract metadata and check if there's a nested responseData
                            const {
                                currentSectionIndex: savedSection,
                                hasConsented: savedConsent,
                                isEligible: savedEligible,
                                lastSaved,
                                progress,
                                status,
                                submittedAt,
                                subjectId,
                                siteId,
                                studyId: _studyId,
                                userId: _userId,
                                _id,
                                id,
                                createdAt,
                                updatedAt,
                                __v,
                                responseId,
                                lastUpdatedAt,
                                data: innerData,
                                success: _success,
                                hasResponses: _hasResponses,
                                ...restData
                            } = submitted;
                            
                            // Try to get form data from multiple possible locations
                            let savedFormData;
                            
                            // Check if innerData (the 'data' property) has actual form fields
                            if (innerData && typeof innerData === 'object' && innerData !== null) {
                                console.log('Found form data in "data" property');
                                console.log('Inner data keys:', Object.keys(innerData));
                                savedFormData = innerData;
                            } 
                            // Otherwise use the remaining data (after removing metadata)
                            else if (restData && Object.keys(restData).length > 0) {
                                console.log('No data in "data" property - using top level data');
                                console.log('Rest data keys:', Object.keys(restData));
                                savedFormData = restData;
                            } 
                            // If both are empty, log warning and show empty form
                            else {
                                console.warn('⚠⚠⚠ NO FORM DATA FOUND ⚠⚠⚠');
                                console.log('innerData:', innerData);
                                console.log('restData keys:', Object.keys(restData));
                                console.log('This form was marked as submitted but contains no field values.');
                                console.log('This could mean:');
                                console.log('  1. The form was submitted empty (no fields filled)');
                                console.log('  2. The backend stored the data in a different format');
                                console.log('  3. The data was submitted by a different user and needs to be queried differently');
                                console.log('Form will load empty - user can fill it in edit mode');
                                savedFormData = {};
                            }
                            
                            console.log('Extracted form data keys:', Object.keys(savedFormData));
                            console.log('Number of form fields:', Object.keys(savedFormData).length);
                            console.log('Form data to load:', JSON.stringify(savedFormData, null, 2));
                            
                            // Check if we have actual form data
                            const hasActualFormData = Object.keys(savedFormData).length > 0;
                            
                            if (!hasActualFormData) {
                                console.warn('⚠ Loading empty form - no previous data found for this subject');
                            }
                            
                            // Load form data
                            setFormData(savedFormData);
                            console.log('✓ Form data set in state');
                            
                            // Verify formData state after setting
                            setTimeout(() => {
                                console.log('=== VERIFYING FORM DATA STATE ===');
                                console.log('Form data in state after 100ms:', savedFormData);
                                console.log('Form data keys:', Object.keys(savedFormData));
                                console.log('==================================');
                            }, 100);
                            
                            // Restore section index
                            if (typeof savedSection === 'number' && savedSection >= 0) {
                                setCurrentSectionIndex(savedSection);
                                console.log('✓ Restored section index:', savedSection);
                            }
                            
                            // Restore consent status
                            if (savedConsent !== undefined) {
                                setHasConsented(savedConsent);
                                console.log('✓ Restored consent status:', savedConsent);
                            }
                            
                            // Restore eligibility status
                            if (savedEligible !== undefined) {
                                setIsEligible(savedEligible);
                                console.log('✓ Restored eligibility status:', savedEligible);
                            }
                            
                            console.log('✓✓✓ Submitted data loaded successfully for editing ✓✓✓');
                        } else {
                            console.error('⚠ Submitted data is not an object:', submitted);
                        }
                    } else {
                        console.warn('⚠ No submitted data found in response');
                    }
                } catch (error) {
                    console.error('❌ Error fetching submitted data:', error);
                    console.error('Error details:', error.message);
                    console.error('Error response:', error.response?.data);
                    // Continue anyway - maybe it's a new form
                }
                
                // Don't set isSubmitted to allow editing
                setIsSubmitted(false);
                setIsLoading(false);
                console.log('Edit mode setup complete - form ready for editing');
                return;
            }

            // For non-edit mode, form is ready to use with static JSON structure
            console.log('✓✓✓ STUDY FORM LOADED SUCCESSFULLY ✓✓✓');

        } catch (err) {
            console.error('=== ERROR DETAILS ===');
            console.error('Error message:', err.message);
            console.error('Error object:', err);
            console.error('====================');
            setError(err.message);
        } finally {
            setIsLoading(false);
        }
    }, [userId, studyId, subject]);

    const updateFormData = (data) => {
        setFormData(prev => ({ ...prev, ...data }));
    };

    const validateAllSections = useCallback(() => {
        const incompleteSections = [];

        if (!studyData || !studyData.sections) {
            return incompleteSections;
        }

        // Helper function to validate fields
        const validateFields = (fields) => {
            const missingFields = [];

            fields.forEach(field => {
                if (field.required) {
                    // Check showIf condition
                    if (field.showIf) {
                        const { field: conditionField, value: conditionValue } = field.showIf;
                        // Skip validation if condition not met
                        if (formData[conditionField] !== conditionValue) {
                            return;
                        }
                    }

                    const value = formData[field.id];
                    if (!value || (Array.isArray(value) && value.length === 0) || value === '') {
                        missingFields.push(field.id);
                    }
                }
            });

            return missingFields;
        };

        // Helper function to get all fields from subsections
        const getAllFieldsFromSection = (section) => {
            const allFields = [];

            if (section.subsections) {
                section.subsections.forEach(subsection => {
                    if (subsection.fields) {
                        allFields.push(...subsection.fields);
                    }
                });
            }

            return allFields;
        };

        // If subject is ineligible, only validate first and last section
        if (isEligible === false) {
            const firstSection = studyData.sections[0];
            const lastSection = studyData.sections[studyData.sections.length - 1];

            [firstSection, lastSection].forEach((section, idx) => {
                const actualIndex = idx === 0 ? 0 : studyData.sections.length - 1;
                const allFields = getAllFieldsFromSection(section);
                const missing = validateFields(allFields);

                if (missing.length > 0) {
                    if (!incompleteSections.includes(actualIndex)) {
                        incompleteSections.push(actualIndex);
                    }
                }
            });
        } else {
            // For eligible subjects, validate all sections
            studyData.sections.forEach((section, index) => {
                const allFields = getAllFieldsFromSection(section);
                const missing = validateFields(allFields);

                if (missing.length > 0) {
                    if (!incompleteSections.includes(index)) {
                        incompleteSections.push(index);
                    }
                }
            });
        }

        return incompleteSections;
    }, [studyData, formData, isEligible]);

    const handleSubmit = async () => {
        if (isSubmitted) {
            return { success: false, error: 'Survey already submitted' };
        }

        setIsSubmitting(true);

        try {
            const submissionData = {
                ...formData,
                submittedAt: new Date().toISOString(),
                progress: 100,
                isEligible: isEligible,
            };

            const response = await submitSurvey(userId, studyId, subject?.siteId, subject?.subjectId, submissionData);

            if (response.success) {
                setIsSubmitted(true);
                return { success: true };
            } else {
                if (response.error && response.error.includes('Duplicate entry')) {
                    setIsSubmitted(true);
                    return { success: false, error: 'This survey has already been submitted.' };
                }
                throw new Error(response.message || response.error || 'Submission failed');
            }
        } catch (error) {
            if (error.message && error.message.includes('Duplicate entry')) {
                setIsSubmitted(true);
                return { success: false, error: 'This survey has already been submitted.' };
            }
            return { success: false, error: error.message };
        } finally {
            setIsSubmitting(false);
        }
    };

    useEffect(() => {
        fetchStudyData();
    }, [fetchStudyData]);

    // Save draft when user leaves the page (beforeunload) or tab becomes hidden
    useEffect(() => {
        if (isSubmitted || isLoading || !studyData || isCRO) return;

        const saveDraftOnLeave = async () => {
            if (Object.keys(formData).length > 0) {
                try {
                    const completionPercentage = calculateFormProgress(studyData, formData);
                    const draftData = {
                        ...formData,
                        currentSectionIndex,
                        hasConsented,
                        isEligible,
                        lastSaved: new Date().toISOString(),
                        progress: completionPercentage,
                    };

                    await saveSurveyDraft(userId, studyId, subject?.siteId, subject?.subjectId, draftData);
                    console.log('Draft saved on page leave');
                } catch (error) {
                    console.error('Failed to save draft on page leave:', error);
                }
            }
        };

        const handleBeforeUnload = (e) => {
            // For beforeunload, we need synchronous call or sendBeacon
            // Use sendBeacon for reliable delivery during page unload
            if (Object.keys(formData).length > 0) {
                const completionPercentage = calculateFormProgress(studyData, formData);
                const draftData = {
                    ...formData,
                    currentSectionIndex,
                    hasConsented,
                    isEligible,
                    lastSaved: new Date().toISOString(),
                    progress: completionPercentage,
                };

                const token = localStorage.getItem('token');
                const payload = JSON.stringify({
                    userId,
                    studyId,
                    siteid: subject?.siteId,
                    subjectId: subject?.subjectId,
                    responseData: draftData
                });

                // Use sendBeacon for reliable delivery during page unload
                const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000';
                navigator.sendBeacon(
                    `${baseUrl}/api/survey/save-draft`,
                    new Blob([payload], { type: 'application/json' })
                );
            }
        };

        const handleVisibilityChange = () => {
            if (document.visibilityState === 'hidden') {
                saveDraftOnLeave();
            }
        };

        window.addEventListener('beforeunload', handleBeforeUnload);
        document.addEventListener('visibilitychange', handleVisibilityChange);

        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, [formData, currentSectionIndex, hasConsented, isEligible, isLoading, studyData, isSubmitted, isCRO, userId, studyId, subject]);

    return {
        studyData,
        currentSectionIndex,
        setCurrentSectionIndex,
        formData,
        updateFormData,
        isSubmitting,
        isSubmitted,
        isLoading,
        error,
        hasConsented,
        setHasConsented,
        isEligible,
        setIsEligible,
        isSavingDraft,
        lastSaveTime,
        autoSaveDraft,
        validateAllSections,
        handleSubmit,
        completionPercentage: calculateFormProgress(studyData, formData)
    };
};